import ks from "../ReUsableTS/kBrowserShared";
import { kEdTblSelectionMode } from "./kEditableTableTypes";
import { kEditableTableRows } from "./kEditableTableRow";
import type { kEditableTableRow } from "./kEditableTableRow";
import type { kEditableCellSelection } from "./kEditableCellSelection";
import type { kEditableTableCell } from "./kEditableTableCell";
import type { kEditableColumn } from "./kEditableColumn";

// Member bodies of kEditableCellSelection moved here verbatim (this -> Self) by the size split of 2026-10-20; every kEditableCellSelection member keeps its signature and delegates here.
export class kEditableCellSelectionQuery {
    public static SetSelectionByIndexes(Self: kEditableCellSelection, SelectionMode:kEdTblSelectionMode): void {
        if (ks.KErrorMode === true) { return; }
        try {
            let CurrRoRowIndex:number = 0;
            let CurrColIndex:number = 0;
            let CurrRow:kEditableTableRow;
            let CurrCell:kEditableTableCell; 
            if(Self.ParentTable){
                for(CurrRoRowIndex=Self.RowIndexStart;CurrRoRowIndex<=Self.RowIndexEnd;CurrRoRowIndex++){
                    CurrRow = Self.ParentTable.Rows.Items[CurrRoRowIndex];
                    for(CurrColIndex = Self.ColindexStart;CurrColIndex<=Self.ColIndexEnd;CurrColIndex++){
                        CurrCell = CurrRow.Cells[CurrColIndex];
                        CurrCell.SelectionMode =  SelectionMode;
                    }
                }
            }
            Self.SelectionMode = SelectionMode;
            Self.UpdateSelectionStyles();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionQuery.SetSelectionByIndexes")
        }
    }

    /** Moved from kEditableCellSelection._CopyHeaderRow (size split); the class member delegates here. */
    public static _CopyHeaderRow(Self: kEditableCellSelection): string {
        if (ks.KErrorMode === true) { return ""; }
        try {
            if (Self.ParentTable === null) { return ""; }
            if (Self.SelectionMode !== kEdTblSelectionMode.SelectAll) { return ""; }
            let HeaderText: string = "";
            let Currcol: kEditableColumn;
            for (Currcol of Self.ParentTable.Columns.Items) {
                HeaderText += Currcol.Title + "\t";
            }
            return HeaderText + "\n";
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionQuery._CopyHeaderRow");
            return "";
        }
    }

    public static GetTopLeftCell(Self: kEditableCellSelection): kEditableTableCell|null {
        if (ks.KErrorMode === true) { return null; }
        try {
            return Self["_TopCell"](Self.ColindexStart, "kEditableCellSelection.GetTopLeftCell");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionQuery.GetTopLeftCell");
            return null;
        }
    }

    /** Moved from kEditableCellSelection._TopCell (size split); the class member delegates here. */
    public static _TopCell(Self: kEditableCellSelection, ColIndex: number, Label: string): kEditableTableCell | null {
        if (ks.KErrorMode === true) { return null; }
        try {
            if(Self.ParentTable){
                const TopRow:kEditableTableRow = Self.ParentTable.Rows.Items[Self.RowIndexStart];
                if(TopRow.Cells.length>0){
                    return TopRow.Cells[ColIndex];
                }
            }
            return null;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionQuery._TopCell");
            return null;
        }
    }

    public static GetTopRigthCell(Self: kEditableCellSelection): kEditableTableCell|null {
        if (ks.KErrorMode === true) { return null; }
        try {
            return Self["_TopCell"](Self.ColIndexEnd, "kEditableCellSelection.GetTopRigthCell");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionQuery.GetTopRigthCell");
            return null;
        }
    }

    public static SelectionChangeMulti(Self: kEditableCellSelection, NewSelectedCell:kEditableTableCell): void {
        if (ks.KErrorMode === true) { return; }
        try {

            Self["_CloseEditorsBeforeMultiSelect"]();
            if (Self._AnchorRow <= NewSelectedCell.RowIndex) {
                Self.RowIndexStart = Self._AnchorRow;
                Self.RowIndexEnd = NewSelectedCell.RowIndex;
            } else {
                Self.RowIndexStart = NewSelectedCell.RowIndex;
                Self.RowIndexEnd = Self._AnchorRow;
            }
            if (Self._AnchorCol <= NewSelectedCell.ColumnIndex) {
                Self.ColindexStart = Self._AnchorCol;
                Self.ColIndexEnd = NewSelectedCell.ColumnIndex;
            } else {
                Self.ColindexStart = NewSelectedCell.ColumnIndex;
                Self.ColIndexEnd = Self._AnchorCol;
            }
            Self["_SetModeFromAnchor"](NewSelectedCell);
            // INCLUSIVE counts (+1). Fixed 2026-07-27 - a 2x2 range used to collapse to one cell.
            //
            // These two lines used to be exclusive, so a 2-row x 2-col rectangle was recorded as
            // 1 x 1. UpdateSelectionStyles calls _CheckSingleCell BEFORE GetSelectedCells, and
            // GetSelectedCells is the only place that re-derives these counts with the +1 - so
            // _CheckSingleCell read 1 x 1, concluded "single cell", and switched SelectionMode to
            // SingleCell. The correction arrived one call too late. Shift-clicking or dragging one
            // cell diagonally from the anchor therefore threw the selection away; every other size
            // worked (2x3 -> 6 cells, 3x3 -> 9), which is why it read as random.
            //
            // The +1 also matches SetSingleRowSelected, which had it all along. Pinned by C15/C16
            // in tests/kEditableCellSelection.characterization.test.js.
            Self.SelectedCols = Self.ColIndexEnd - Self.ColindexStart + 1;
            Self.SelectedRows = Self.RowIndexEnd - Self.RowIndexStart + 1;
            Self.UpdateSelectionStyles();
       } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionQuery.SelectionChangeMulti");
            return;
        }
    }

    /** Moved from kEditableCellSelection._CloseEditorsBeforeMultiSelect (size split); the class member delegates here. */
    public static _CloseEditorsBeforeMultiSelect(Self: kEditableCellSelection): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Self.ParentTable === null) { return; }
            if (Self.ParentTable.CurrenEditableCell) {
                Self.ParentTable.CurrenEditableCell.SetCellReadMode();
                Self.ParentTable.CurrenEditableCell = null;
            }
            if (Self.ParentTable.RowContextMenu) {
                Self.ParentTable.RowContextMenu.OutsideClick();
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionQuery._CloseEditorsBeforeMultiSelect");
        }
    }

    /** Moved from kEditableCellSelection._SetModeFromAnchor (size split); the class member delegates here. */
    public static _SetModeFromAnchor(Self: kEditableCellSelection, NewSelectedCell:kEditableTableCell): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Self._AnchorRow <= NewSelectedCell.RowIndex) {
                if (Self._AnchorCol <= NewSelectedCell.ColumnIndex) {
                    Self.SelectionMode = kEdTblSelectionMode.CustomExtRightDown;
                } else {
                    Self.SelectionMode = kEdTblSelectionMode.CustomExtLeftDown;
                }
            } else {
                if (Self._AnchorCol <= NewSelectedCell.ColumnIndex) {
                    Self.SelectionMode = kEdTblSelectionMode.CustomExtLeftUp;
                } else {
                    Self.SelectionMode = kEdTblSelectionMode.CustomExtRightUp;
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionQuery._SetModeFromAnchor");
        }
    }

    public static GetSelectedCells(Self: kEditableCellSelection): kEditableTableCell[] {
        if (ks.KErrorMode === true) { return []; }
        try {
            const SelectedCells:kEditableTableCell[] = Self["_CollectSelectedCells"]();
            if(Self.SelectionMode!== kEdTblSelectionMode.NoSelection){
                Self.SelectedCols = Self.ColIndexEnd - Self.ColindexStart +1;
                Self.SelectedRows = Self.RowIndexEnd - Self.RowIndexStart +1;
            }else{
                Self.SelectedCols = 0;
                Self.SelectedRows = 0;
            }
            return SelectedCells;
       } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionQuery.GetSelectedCells");
            return [];
        }
    }

    /** Moved from kEditableCellSelection._CollectSelectedCells (size split); the class member delegates here. */
    public static _CollectSelectedCells(Self: kEditableCellSelection): kEditableTableCell[] {
        if (ks.KErrorMode === true) { return []; }
        try {
            const SelectedCells:kEditableTableCell[] = [];
            if(Self.ParentTable === null){ return SelectedCells; }
            switch (Self.SelectionMode){
                case kEdTblSelectionMode.SingleCell: Self["_CollectSingleCell"](SelectedCells); break;
                case kEdTblSelectionMode.SingleColumn: Self["_CollectSingleColumn"](SelectedCells); break;
                case kEdTblSelectionMode.MultipleColsExtendRight:
                case kEdTblSelectionMode.MultipleColsExtendLeft: Self["_CollectColumnRange"](SelectedCells); break;
                case kEdTblSelectionMode.SingleRow: Self["_CollectSingleRow"](SelectedCells); break;
                case kEdTblSelectionMode.MultipleRowsExtendDown:
                case kEdTblSelectionMode.MultipleRowsExtendUp:
                case kEdTblSelectionMode.SelectAll: Self["_CollectRowRange"](SelectedCells); break;
                case kEdTblSelectionMode.CustomExtRightDown:
                case kEdTblSelectionMode.CustomExtLeftDown:
                case kEdTblSelectionMode.CustomExtRightUp:
                case kEdTblSelectionMode.CustomExtLeftUp: Self["_CollectRectangle"](SelectedCells); break;
            }
            return SelectedCells;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionQuery._CollectSelectedCells");
            return [];
        }
    }

    /** Moved from kEditableCellSelection._CollectSingleCell (size split); the class member delegates here. */
    public static _CollectSingleCell(Self: kEditableCellSelection, SelectedCells:kEditableTableCell[]): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if(Self.ParentTable === null){ return; }
            if(Self.ParentTable.CurrenEditableCell){
                SelectedCells.push(Self.ParentTable.CurrenEditableCell);
            }else{
                Self.SelectionMode = kEdTblSelectionMode.NoSelection;
                console.log("Wrong selection mode!")
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionQuery._CollectSingleCell");
        }
    }

    /** Moved from kEditableCellSelection._CollectSingleColumn (size split); the class member delegates here. */
    public static _CollectSingleColumn(Self: kEditableCellSelection, SelectedCells:kEditableTableCell[]): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if(Self.ParentTable === null){ return; }
            let CurrRow:kEditableTableRow;
            for(CurrRow of Self.ParentTable.Rows.Items){
                SelectedCells.push(CurrRow.Cells[Self.ColindexStart]);
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionQuery._CollectSingleColumn");
        }
    }

    /** Moved from kEditableCellSelection._CollectBlock (size split); the class member delegates here. */
    public static _CollectBlock(Self: kEditableCellSelection, SelectedCells:kEditableTableCell[], RowsFirst: boolean, RowStart: number, RowEnd: number, ColStart: number, ColEnd: number): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if(Self.ParentTable === null){ return; }
            let Counter:number = 0;
            let Counter2:number = 0;
            let CurrRow:kEditableTableRow;
            if (RowsFirst === true) {
                for(Counter = RowStart;Counter <= RowEnd;Counter ++){
                    CurrRow = Self.ParentTable.Rows.Items[Counter];
                    for(Counter2 = ColStart;Counter2 <= ColEnd;Counter2++){
                        SelectedCells.push(CurrRow.Cells[Counter2]);
                    }
                }
                return;
            }
            for(Counter = ColStart;Counter <= ColEnd;Counter ++){
                for(Counter2 = RowStart;Counter2 <= RowEnd;Counter2++){
                    SelectedCells.push(Self.ParentTable.Rows.Items[Counter2].Cells[Counter]);
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionQuery._CollectBlock");
        }
    }

    /** Moved from kEditableCellSelection._CollectColumnRange (size split); the class member delegates here. */
    public static _CollectColumnRange(Self: kEditableCellSelection, SelectedCells:kEditableTableCell[]): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if(Self.ParentTable === null){ return; }
            Self["_CollectBlock"](SelectedCells, false, 0, Self.ParentTable.Rows.Items.length - 1, Self.ColindexStart, Self.ColIndexEnd);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionQuery._CollectColumnRange");
        }
    }

    /** Moved from kEditableCellSelection._CollectSingleRow (size split); the class member delegates here. */
    public static _CollectSingleRow(Self: kEditableCellSelection, SelectedCells:kEditableTableCell[]): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if(Self.ParentTable === null){ return; }
            let CurrCell:kEditableTableCell;
            const CurrRow:kEditableTableRow = Self.ParentTable.Rows.Items[Self.RowIndexStart];
            for(CurrCell of CurrRow.Cells){
                SelectedCells.push(CurrCell);
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionQuery._CollectSingleRow");
        }
    }

    /** Moved from kEditableCellSelection._CollectRowRange (size split); the class member delegates here. */
    public static _CollectRowRange(Self: kEditableCellSelection, SelectedCells:kEditableTableCell[]): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if(Self.ParentTable === null){ return; }
            let Counter:number = 0;
            let CurrRow:kEditableTableRow;
            let CurrCell:kEditableTableCell;
            for(Counter = Self.RowIndexStart;Counter<=Self.RowIndexEnd;Counter ++){
                CurrRow = Self.ParentTable.Rows.Items[Counter]
                for(CurrCell of CurrRow.Cells){
                    SelectedCells.push(CurrCell);
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionQuery._CollectRowRange");
        }
    }

    /** Moved from kEditableCellSelection._CollectRectangle (size split); the class member delegates here. */
    public static _CollectRectangle(Self: kEditableCellSelection, SelectedCells:kEditableTableCell[]): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if(Self.ParentTable === null){ return; }
            Self["_CollectBlock"](SelectedCells, true, Self.RowIndexStart, Self.RowIndexEnd, Self.ColindexStart, Self.ColIndexEnd);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionQuery._CollectRectangle");
        }
    }

    public static GetSelectedRows(Self: kEditableCellSelection): kEditableTableRows {
        if (ks.KErrorMode === true) { return new kEditableTableRows(); }
        try {
            const SelectedRows:kEditableTableRows = new kEditableTableRows();
            let RowCounter:number = 0;
            if(Self.ParentTable){
                for(RowCounter=Self.RowIndexStart;RowCounter<=Self.RowIndexEnd;RowCounter++){
                    SelectedRows.push(Self.ParentTable.Rows.Items[RowCounter]);
                }
            }
            return SelectedRows;
       } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionQuery.GetSelectedRows");
            return new kEditableTableRows();
        }
    }

    // Paint the dashed selection-range perimeter. Derives the bounding box from
    // the selected cells themselves (correct for row / column / range modes alike)
    // and toggles the four edge classes so each boundary cell draws only its outer
    // side(s). The dashes survive a coloured column because a border is not a
    // background (see the td.kSelEdge* CSS).
    public static _PaintRangeEdges(Self: kEditableCellSelection, SelectedCells: kEditableTableCell[]): void {
        if (ks.KErrorMode === true) { return; }
        try {
            let MinRow: number = Number.MAX_SAFE_INTEGER;
            let MaxRow: number = Number.MIN_SAFE_INTEGER;
            let MinCol: number = Number.MAX_SAFE_INTEGER;
            let MaxCol: number = Number.MIN_SAFE_INTEGER;
            for (const BoundsCell of SelectedCells) {
                if (BoundsCell.RowIndex < MinRow) { MinRow = BoundsCell.RowIndex; }
                if (BoundsCell.RowIndex > MaxRow) { MaxRow = BoundsCell.RowIndex; }
                if (BoundsCell.ColumnIndex < MinCol) { MinCol = BoundsCell.ColumnIndex; }
                if (BoundsCell.ColumnIndex > MaxCol) { MaxCol = BoundsCell.ColumnIndex; }
            }
            for (const EdgeCell of SelectedCells) {
                Self["_SetSelectionEdges"](
                    EdgeCell,
                    EdgeCell.RowIndex === MinRow,
                    EdgeCell.ColumnIndex === MaxCol,
                    EdgeCell.RowIndex === MaxRow,
                    EdgeCell.ColumnIndex === MinCol);
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionQuery._PaintRangeEdges");
            return;
        }
    }

    // Toggle the four dashed-perimeter edge classes on ONE selected cell. A corner
    // cell gets two true flags, so its single ::after overlay paints two sides.
    public static _SetSelectionEdges(Self: kEditableCellSelection, CurrCell: kEditableTableCell, Top: boolean, Right: boolean, Bottom: boolean, Left: boolean): void {
        if (ks.KErrorMode === true) { return; }
        try {
            CurrCell.Cell.classList.toggle("kSelEdgeTop", Top);
            CurrCell.Cell.classList.toggle("kSelEdgeRight", Right);
            CurrCell.Cell.classList.toggle("kSelEdgeBottom", Bottom);
            CurrCell.Cell.classList.toggle("kSelEdgeLeft", Left);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionQuery._SetSelectionEdges");
            return;
        }
    }

    // Strip all four dashed-perimeter edge classes (a cell leaving the selection,
    // or a range collapsing to a single-cell selection).
    public static _ClearSelectionEdges(Self: kEditableCellSelection, CurrCell: kEditableTableCell): void {
        if (ks.KErrorMode === true) { return; }
        try {
            CurrCell.Cell.classList.remove("kSelEdgeTop");
            CurrCell.Cell.classList.remove("kSelEdgeRight");
            CurrCell.Cell.classList.remove("kSelEdgeBottom");
            CurrCell.Cell.classList.remove("kSelEdgeLeft");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionQuery._ClearSelectionEdges");
            return;
        }
    }

    // Clear the selection classes (incl. dashed edges) from cells that were
    // selected last paint but are no longer in the current selection.
    public static _ClearDeselectedCells(Self: kEditableCellSelection, SelectedIds: Set<number>): void {
        if (ks.KErrorMode === true) { return; }
        try {
            for (const PrevCell of Self["_PrevSelectedCells"]) {
                if (!SelectedIds.has(PrevCell.UniqueID)) {
                    PrevCell.Cell.classList.remove("SelectedCellMulti");
                    PrevCell.Cell.classList.remove("SelectedCellSingle");
                    PrevCell.Cell.classList.remove("SelectedFirstRow");
                    Self["_ClearSelectionEdges"](PrevCell);
                    PrevCell.Cell.setAttribute("aria-selected", "false");
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionQuery._ClearDeselectedCells");
            return;
        }
    }

    // Paint a multi-cell (range / row / column) selection: drop any open editor,
    // mark every cell, emphasise the anchor row, then draw the dashed perimeter.
    public static _PaintMultiSelection(Self: kEditableCellSelection, SelectedCells: kEditableTableCell[]): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Self.ParentTable && Self.ParentTable.CurrenEditableCell) {
                Self.ParentTable.CurrenEditableCell.SetCellReadMode();
            }
            for (const CurrCell of SelectedCells) {
                CurrCell.Cell.classList.add("SelectedCellMulti");
                CurrCell.Cell.classList.remove("SelectedCellSingle");
                CurrCell.Cell.setAttribute("aria-selected", "true");
                if (CurrCell.RowIndex === Self.RowIndexStart && Self.SelectedRows > 1) {
                    CurrCell.Cell.classList.add("SelectedFirstRow");
                } else {
                    CurrCell.Cell.classList.remove("SelectedFirstRow");
                }
            }
            Self["_PaintRangeEdges"](SelectedCells);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionQuery._PaintMultiSelection");
            return;
        }
    }

    // Keeps the grid's aria-activedescendant pointing at the cell the user has
    // just moved to. The grid is a composite widget: DOM focus stays on the
    // <table> (the single tab stop) and the ACTIVE cell is announced through
    // this attribute, which is the standard alternative to a roving tabindex
    // and is the only one that does not fight the inline contentEditable /
    // dropdown / date-picker editors this grid opens inside its own cells.
    public static _SyncActiveDescendant(Self: kEditableCellSelection, SelectedCells: kEditableTableCell[]): void {
        if (ks.KErrorMode === true) { return; }
        try {
            // `Table` is declared non-nullable on kEditableTable, so `=== null` never
            // fired there - but this engine is deliberately host-agnostic (its coupling
            // surface is Rows/Columns/GetCellByIndexes/CurrenEditableCell/RowContextMenu/
            // SelectionChangedCB), and a host that supplies no Table left it UNDEFINED,
            // sailed past the null check and threw on setAttribute. That throw reaches
            // KGlobalErrorHandler, which flips KErrorMode - and every guarded paint call
            // after it then silently no-ops, so the whole selection stops rendering with
            // no visible cause. aria-activedescendant is an enhancement: skip it when the
            // host has no grid element rather than taking the selection down with it.
            if (Self.ParentTable === null || !Self.ParentTable.Table) { return; }
            if (SelectedCells.length === 0) {
                Self.ParentTable.Table.removeAttribute("aria-activedescendant");
                return;
            }
            const ActiveCell: kEditableTableCell = SelectedCells[0];
            if (ks.Nz(ActiveCell.Cell.id) === "") {
                ActiveCell.Cell.id = Self.ParentTable.IdPrefix + "-cell-" + ActiveCell.UniqueID.toString();
            }
            Self.ParentTable.Table.setAttribute("aria-activedescendant", ActiveCell.Cell.id);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionQuery._SyncActiveDescendant");
        }
    }

    public static UpdateSelectionStyles(Self: kEditableCellSelection): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self["_CheckSingleCell"]();
            const SelectedCells: kEditableTableCell[] = Self.GetSelectedCells();
            const SelectedIds: Set<number> = new Set<number>();
            for (const CurrCell of SelectedCells) {
                SelectedIds.add(CurrCell.UniqueID);
            }
            Self["_SyncActiveDescendant"](SelectedCells);
            if (Self.ParentTable) {
                Self["_ClearDeselectedCells"](SelectedIds);
                if (Self.SelectionMode === kEdTblSelectionMode.SingleCell) {
                    if (SelectedCells.length > 0) {
                        SelectedCells[0].Cell.classList.remove("SelectedCellMulti");
                        SelectedCells[0].Cell.classList.add("SelectedCellSingle");
                        SelectedCells[0].Cell.setAttribute("aria-selected", "true");
                        Self["_ClearSelectionEdges"](SelectedCells[0]);
                    }
                } else {
                    Self["_PaintMultiSelection"](SelectedCells);
                }
                Self["_PrevSelectedCells"] = SelectedCells;
                if (Self.ParentTable && Self.ParentTable.SelectionChangedCB) {
                    Self.ParentTable.SelectionChangedCB();
                }
            }
            if (SelectedIds.size > 1) {
                Self.ClearTextSelection();
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionQuery.UpdateSelectionStyles");
        }
    }

    public static SelectLastCell(Self: kEditableCellSelection): void {
        if (ks.KErrorMode === true) { return; }
        try {
            let CurrRow:kEditableTableRow;
            if(Self.ParentTable){
                CurrRow = Self.ParentTable.Rows.Items[Self.RowIndexStart];
                CurrRow.SelectLastCell();

            }
       } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionQuery.SelectLastCell");
        }
    }

    public static SelectFirstCell(Self: kEditableCellSelection): void {
        if (ks.KErrorMode === true) { return; }
        try {
            let CurrRow:kEditableTableRow;
            if(Self.ParentTable){
                CurrRow = Self.ParentTable.Rows.Items[Self.RowIndexStart];
                CurrRow.SelectFirstCell();
            }
       } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionQuery.SelectFirstCell");
        }
    }

    public static SelectCellByIndexes(Self: kEditableCellSelection, RowIndex:number, ColIndex:number): void {
        if (ks.KErrorMode === true) { return ; }
        try {
            if(Self.ParentTable){
                // Navigation must not multiply empty trailing rows: pass
                // SuppressGrowOnEmptyLastRow so stepping past the end only grows
                // when the last row actually holds content (typed or committed).
                let CurrCell:kEditableTableCell = Self.ParentTable.GetCellByIndexes(RowIndex,ColIndex,true);
                if (CurrCell.ColDef && CurrCell.ParentTable){
                    CurrCell.Select();
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionQuery.SelectCellByIndexes");
            return ;
        }
    }
}
